import { useState } from "react";
import { Link } from "react-router-dom";
import { formatGbp } from "../commerce/pricing";
import { routes } from "../config/routes";
import { siteConfig } from "../config/site";
import {
  categoryLabel,
  findCategory,
  isProductPhotograph,
  pendingLaunchCheckLabels,
  relatedPreviewProducts,
  type Product
} from "../data/catalogue";
import { DeliveryNotice } from "./DeliveryNotice";
import { ProductCard } from "./Cards";
import { ProductFactsSections } from "./ProductFacts";

interface ProductDetailsProps {
  product: Product;
}

export function ProductDetails({ product }: ProductDetailsProps) {
  const [activeImage, setActiveImage] = useState(0);
  const price = formatGbp(product.retailPricePence);
  const category = findCategory(product.category);
  const related = relatedPreviewProducts(product);
  const pendingChecks = pendingLaunchCheckLabels(product);
  const currentImage = product.images[activeImage] ?? product.images[0];

  return (
    <div className="product-page">
      <div className="product-layout">
        <div className="product-gallery">
        <div
          className={`product-main-image${isProductPhotograph(currentImage) ? " product-image-photo" : ""}`}
        >
          <img
            src={currentImage?.src}
            alt={currentImage?.alt}
            width="800"
            height="800"
            decoding="async"
            fetchPriority="high"
          />
          <span className="status-pill">Preview</span>
        </div>
        {product.images.length > 1 && (
          <div className="product-thumbs" role="tablist" aria-label="Product photographs">
            {product.images.map((image, index) => (
              <button
                key={image.src}
                type="button"
                className={index === activeImage ? "is-active" : ""}
                onClick={() => setActiveImage(index)}
                aria-label={`Show photograph ${index + 1}`}
              >
                <img src={image.src} alt="" width="120" height="120" />
              </button>
            ))}
          </div>
        )}
        </div>
        <div className="product-info">
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to={routes.shop}>Shop</Link>
          {category && (
            <>
              <span>/</span>
              <Link to={`${routes.shop}/${category.id}`}>{category.name}</Link>
            </>
          )}
          <span>/</span>
          <span>{product.name}</span>
        </nav>
        {product.brand && <p className="eyebrow">{product.brand}</p>}
        <h1>{product.name}</h1>
        <p className="product-meta">
          {categoryLabel(product)}
          {product.packSize ? ` · ${product.packSize}` : ""}
        </p>
        {price && (
          <p className="product-price preview-price">
            <span className="preview-price-label">Preview price</span>
            <span>{price}</span>
          </p>
        )}
        <div className="notice notice-warm">
          <strong>Ordering is not yet available</strong>
          <p>
            This is a preview listing. {siteConfig.prelaunchNotice} Delivery is charged
            separately; the delivery amount has not been set yet.
          </p>
        </div>
        {product.needsElectricalReview && (
          <div className="notice">
            <strong>Beauty device under review</strong>
            <p>
              This listing is a beauty device and is held for a separate product-safety
              review before it can be offered for sale.
            </p>
          </div>
        )}
        <DeliveryNotice />
        {pendingChecks.length > 0 && (
          <details>
            <summary>Still to confirm before sale</summary>
            <p>
              This preview is not approved for sale. Remaining checks:{" "}
              {pendingChecks.join(", ")}.
            </p>
          </details>
        )}
      </div>
      </div>
      <ProductFactsSections product={product} />
      {related.length > 0 && (
        <div className="related-products">
          <h2>More in {categoryLabel(product)}</h2>
          <div className="product-grid product-grid-related">
            {related.map((item) => (
              <ProductCard key={item.id} product={item} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
